/**
 * The box-naming rule — one owner of "does this name already say BOX?".
 *
 * # Why this exists (Chapter 10, F22)
 *
 * A box's public name comes from a resolution chain the operator controls:
 * curated category label → flow title → humanised id. Callers then rendered
 * `` `${resolvedName} BOX` `` — appending the word unconditionally. That is
 * right for the two live boxes (their curated labels are "SPRING" and
 * "OFFICE") and wrong for every box created in the Creation Lab, which falls
 * through to the flow title: an operator naming a box "TeamBuilding Box"
 * shipped "TEAMBUILDING BOX BOX" to the configurator header, the cart, the
 * quote page AND the customer's proposal email.
 *
 * Two directions, because the codebase needs both:
 *
 *   - {@link withBoxSuffix} — the display name. Used wherever a box is
 *     titled for a customer.
 *   - {@link stripBoxSuffix} — the bare name, for a reader that supplies the
 *     word itself. The email templates (authored content, `{{ box.themeId }}
 *     BOX`) and the configurator header (which renders "BOX" as its own
 *     styled span) are both in that class, so the token they read must NOT
 *     already carry it.
 *
 * # The boundary rule
 *
 * The word is only considered present when it stands alone at the end of the
 * name — after whitespace, or as the whole name. "TOOLBOX" is a different
 * word, so it still earns a suffix ("TOOLBOX BOX"); a name with BOX in the
 * middle ("OFFICE BOX STUDIO") does too, because nothing is doubled there.
 */

/** The word this module appends. Exported so no caller re-spells it. */
export const BOX_WORD = 'BOX';

/**
 * Matches a trailing, free-standing "box" — either preceded by whitespace or
 * making up the entire name. Case-insensitive: an operator types "Box".
 */
const TRAILING_BOX = /(^|\s+)box$/i;

/**
 * The customer-facing display name for a box: the resolved name plus the word
 * BOX, unless it already ends with that word.
 *
 * A blank name yields a blank string rather than a bare "BOX" — an empty name
 * means the resolution chain found nothing, and labelling a box " BOX" is
 * worse than labelling it nothing.
 */
export function withBoxSuffix(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '';
  if (TRAILING_BOX.test(trimmed)) return trimmed;
  return `${trimmed} ${BOX_WORD}`;
}

/**
 * The bare name, for a reader that appends the word BOX itself.
 *
 * A name that IS the word ("BOX") is returned unchanged — stripping it would
 * render the email header as "ORDER DETAILS:  BOX".
 */
export function stripBoxSuffix(name: string): string {
  const trimmed = name.trim();
  const match = TRAILING_BOX.exec(trimmed);
  // No trailing word, or the word is the whole name (no leading whitespace
  // captured) → nothing to strip.
  if (!match || match[1] === '') return trimmed;
  return trimmed.slice(0, match.index);
}
